import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, TrendingUp, ArrowLeftRight, Key, RefreshCw, Download } from "lucide-react";
import axios from "axios";
import { consolidateTickets, applyExactaSingleMin, comboToXpress, amwagerComboField } from "../lib/ticketConsolidation";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function todayISO() {
  // Racing day is ET-anchored; use ET so a late-evening (e.g. 10pm EST) view
  // doesn't roll to the next calendar day the way UTC would.
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date());
}

// ADW exporters — reuse BetMaker's exacta format from each qualifying combo + its stake.
// consolidateTickets expects a `combo` string ("a-b") which it parses into legs.
function ledgerTickets(ledger) {
  return ledger.combos.map((c) => ({ combo: c.combo, amount: c.stake }));
}
function amwagerLines(track, race, tickets, date) {
  const d = date.replace(/-/g, ""); const pre = track.slice(0, -1), suf = track.slice(-1);
  const cons = consolidateTickets(tickets, true); applyExactaSingleMin(cons, "EX", 1, 2);
  return cons.map((t) => `,,${d},USA,${pre},${suf},EX,,${race},${amwagerComboField(t.legs)},USD,${t.amount.toFixed(2)},,,`).join("\n");
}
function xpressLines(track, race, tickets, date) {
  const cons = consolidateTickets(tickets, true); applyExactaSingleMin(cons, "EX", 1, 2);
  return cons.map((t) => `${date},${track},${race},EXACTA,${comboToXpress(t.legs)},${t.amount.toFixed(2)},,,WHEEL`).join("\n");
}
function downloadFile(content, filename) {
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
function handleAdwDownload(race, date, kind) {
  if (!race.ledger.combos.length) return;
  const code = kind === "amwager" ? race.amcode : race.xbcode;
  const builder = kind === "amwager" ? amwagerLines : xpressLines;
  const content = builder(code, race.race_number, ledgerTickets(race.ledger), date);
  downloadFile(content, `${kind}_exacta_${date}_${code}_r${race.race_number}.txt`);
  axios.post(`${API_URL}/api/quickpick/log-download`, {
    kind, app: "exacta-value", track: code, race: race.race_number, date, pool: "EX",
    amount: race.ledger.wagered,
  }, { withCredentials: true }).catch(() => {});
}

function RoiStat({ label, value, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-800 text-slate-200 border-slate-700",
    green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    red: "bg-red-500/15 text-red-400 border-red-500/40",
  };
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold ${tones[tone]}`}>
      {label} <span className="font-bold">{value}</span>
    </span>
  );
}

function RaceCard({ race, date, isAdmin }) {
  const L = race.ledger;
  const settled = race.settled;
  const roi = settled ? L.roi_pct : L.expected_roi_pct;
  const roiTone = roi == null ? "slate" : roi >= 0 ? "green" : "red";
  return (
    <div data-testid={`exacta-race-${race.race_key}`}
      className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-white">{race.track_name}</span>
          <span className="text-sm font-semibold text-emerald-400">Race {race.race_number}</span>
          {!race.actionable && (() => {
            // Derive MTP from post_time (self-correcting) — the feed cycle's mtp
            // freezes during a prices gap (e.g. stuck at 2 while post time passes).
            let mtp = race.snapshot_mtp;
            if (race.post_time) {
              const diffMin = Math.round((new Date(race.post_time).getTime() - Date.now()) / 60000);
              if (!Number.isNaN(diffMin)) mtp = diffMin;
            }
            const label = (mtp != null && mtp >= 1)
              ? `Coming Up - ${mtp}MTP`
              : "Awaiting Pool Confirmation - 0MTP";
            return (
              <span className="text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-amber-500/15 text-amber-300 border border-amber-500/40"
                title="Waiting for the pool to confirm at post — not yet actionable">{label}</span>
            );
          })()}
          {race.actionable && !race.is_final && (
            <span data-testid={`locked-badge-${race.race_key}`}
              className="text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
              title="Snapshot locked at post — pool no longer updates">Locked</span>
          )}
          {race.is_final && (
            <span data-testid={`final-badge-${race.race_key}`}
              className="text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-slate-600/40 text-slate-300 border border-slate-500/50"
              title="Results in / final pool">FINAL</span>
          )}
          {isAdmin && race.actionable && (
            <span data-testid={`zc-${race.race_key}`}
              className="text-[10px] font-mono rounded px-1.5 py-0.5 bg-slate-800 text-slate-400 border border-slate-700"
              title={`ZeroCycle count · trigger: ${race.post_trigger || "post"}`}>
              ZC:{race.zero_cycle_count || 0}{race.post_trigger === "zero3" ? "·fb" : ""}
            </span>
          )}
          {race.keys && race.keys.length > 0 && (
            <span data-testid={`keys-${race.race_key}`}
              className="inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 bg-indigo-500/15 text-indigo-300 border border-indigo-500/40"
              title="Consensus / must-use key horses">
              <Key className="w-3 h-3" /> Keys {race.keys.join(", ")}
            </span>
          )}
          {race.fades && race.fades.length > 0 && (
            <span data-testid={`fades-${race.race_key}`}
              className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-red-500/10 text-red-400/80 border border-red-500/30"
              title="Fade runners — excluded from all combos">
              Fades excl: {race.fades.join(", ")}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {L.qualified > 0 && (
            <>
              <button onClick={() => handleAdwDownload(race, date, "amwager")}
                data-testid={`dl-amw-${race.race_key}`}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
                title="Download AmWager exacta file">
                <Download className="w-3.5 h-3.5" /> AmW
              </button>
              <button onClick={() => handleAdwDownload(race, date, "xpressbet")}
                data-testid={`dl-xb-${race.race_key}`}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
                title="Download Xpressbet exacta file">
                <Download className="w-3.5 h-3.5" /> XB
              </button>
            </>
          )}
          <RoiStat label="Wagered" value={`$${L.wagered.toFixed(2)}`} testid={`wagered-${race.race_key}`} />
          {settled && <RoiStat label="Returned" value={`$${(L.returned || 0).toFixed(2)}`} testid={`returned-${race.race_key}`} />}
          <RoiStat label={settled ? "ROI" : "Exp. ROI"} value={roi == null ? "—" : `${roi}%`} tone={roiTone} testid={`roi-${race.race_key}`} />
        </div>
      </div>

      {L.qualified === 0 ? (
        <p className="text-sm text-slate-500">
          {race.model_available
            ? "No combinations clear the 20% edge in this race."
            : "Projected odds not yet available for this race — no value signal until the model prices it."}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid={`chips-${race.race_key}`}>
          {L.combos.map((c) => {
            const won = c.won;
            const key = c.key_included;
            return (
              <div key={c.combo}
                data-testid={`chip-${race.race_key}-${c.combo}`}
                title={`EV ${c.ev} · fair ${(c.p_fair * 100).toFixed(2)}% ($${c.p_fair > 0 ? (1 / c.p_fair).toFixed(2) : "—"} calc) · price $${c.price.toFixed(2)}${key ? " · includes a consensus key" : ""}`}
                className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm border transition
                  ${won ? "bg-emerald-500/25 border-emerald-400 text-emerald-200"
                        : key ? "bg-indigo-500/15 border-indigo-500/50 text-indigo-200"
                              : "bg-slate-800 border-slate-700 text-slate-200"}`}>
                <span className="text-[10px] font-bold text-slate-400">EX</span>
                {key && <Key className="w-3 h-3 text-indigo-300" />}
                <span className="font-semibold tracking-wide">{c.combo}</span>
                <span className="font-bold text-amber-300">${c.stake.toFixed(2)}</span>
                {won && <span className="text-[10px] font-bold text-emerald-300">WON</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ExactaValue() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [date, setDate] = useState(todayISO());
  const [sort, setSort] = useState("post_desc");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [upcomingOnly, setUpcomingOnly] = useState(true);
  const [trackFilter, setTrackFilter] = useState("all");
  const [wager, setWager] = useState("100");
  const [evPct, setEvPct] = useState(20);        // admin-only EV threshold override (%)
  const [refreshSec, setRefreshSec] = useState(() => {
    const raw = localStorage.getItem("exacta_refresh_sec");
    if (raw === null) return 60;  // default 60s
    const n = Number(raw);
    return [0, 15, 30, 60].includes(n) ? n : 60;  // remember last valid choice
  });

  // seed the wager from the user's Default Bet Amount preference (bankroll)
  useEffect(() => {
    axios.get(`${API_URL}/api/betmaker/preferences`, { withCredentials: true })
      .then((r) => { const b = r.data?.preferences?.bankroll; if (b) setWager(String(b)); })
      .catch(() => {});
  }, []);

  const reqIdRef = useRef(0);
  const load = useCallback(async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    setError("");
    try {
      const b = parseFloat(wager) > 0 ? parseFloat(wager) : 100;
      let url = `${API_URL}/api/exacta-value/races?date=${date}&sort=${sort}&budget=${b}`;
      if (isAdmin) url += `&ev_gate=${(1 + (Number(evPct) || 20) / 100).toFixed(4)}`;
      const res = await fetch(url, { credentials: "include" });
      const json = await res.json();
      if (myId !== reqIdRef.current) return;  // ignore out-of-order/stale responses
      if (!res.ok) throw new Error(json.detail || "Failed to load");
      setData(json);
    } catch (e) {
      if (myId !== reqIdRef.current) return;
      setError(e.message);
      setData(null);
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  }, [date, sort, wager, isAdmin, evPct]);

  useEffect(() => { load(); }, [load]);
  // Default "Upcoming Only" ON for today/future dates, OFF when reviewing a past date.
  useEffect(() => { setUpcomingOnly(date >= todayISO()); }, [date]);
  // Auto-refresh (60s default, persisted; admins can change the interval).
  // Only refresh when viewing today (ET) — a prior date's data is static.
  useEffect(() => {
    if (!refreshSec || date !== todayISO()) return undefined;
    const id = setInterval(() => { load(); }, refreshSec * 1000);
    return () => clearInterval(id);
  }, [refreshSec, load, date]);

  const races = data?.races || [];
  const trackOptions = Array.from(new Map(races.map((r) => [r.track_code, r.track_name])).entries())
    .sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  const byTrack = trackFilter === "all" ? races : races.filter((r) => r.track_code === trackFilter);
  // "Graded" = settled races we actually bet on (wagered > 0); $0-wager races
  // (no model / no qualifying combos) aren't a graded result.
  const settledRaces = byTrack.filter((r) => r.settled && (r.ledger.wagered || 0) > 0);
  const cumW = settledRaces.reduce((s, r) => s + (r.ledger.wagered || 0), 0);
  const cumR = settledRaces.reduce((s, r) => s + (r.ledger.returned || 0), 0);
  const cumRoi = cumW ? Math.round(((cumR - cumW) / cumW) * 1000) / 10 : null;
  const listRaces = byTrack.filter((r) => !upcomingOnly || !r.settled);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/60 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            <h1 className="text-lg font-bold">Exacta Value</h1>
            <span className="text-xs text-slate-500 hidden sm:inline">market-mispriced combinations</span>
          </div>
          <Link to="/" data-testid="exacta-back-link"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm transition-transform hover:scale-105">
            <ArrowLeftRight className="w-4 h-4" /> IntelliTix
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-5 space-y-4">
        {/* controls */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-slate-400">Date
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              data-testid="exacta-date-input"
              className="ml-2 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
          </label>
          <div className="inline-flex rounded-md border border-slate-700 overflow-hidden" data-testid="exacta-sort-toggle">
            <button onClick={() => setSort("post_desc")} data-testid="sort-post"
              className={`px-3 py-1 text-xs font-medium ${sort === "post_desc" ? "bg-emerald-500 text-slate-950" : "bg-slate-800 text-slate-300"}`}>
              Post time
            </button>
            <button onClick={() => setSort("track")} data-testid="sort-track"
              className={`px-3 py-1 text-xs font-medium ${sort === "track" ? "bg-emerald-500 text-slate-950" : "bg-slate-800 text-slate-300"}`}>
              Track
            </button>
          </div>
          <label className="text-sm text-slate-400">Wager $
            <input type="number" min="1" value={wager} onChange={(e) => setWager(e.target.value)}
              data-testid="exacta-wager-input"
              className="ml-1 w-20 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100"
              title="Per-race dutch total (seeded from your Default Bet Amount; overrides here)" />
          </label>
          <button onClick={load} data-testid="exacta-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs rounded border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          {isAdmin && (
            <label className="text-sm text-amber-400/90" title="Admin: minimum edge to qualify a combo">
              EV %
              <input type="number" min="0" step="1" value={evPct} onChange={(e) => setEvPct(e.target.value)}
                data-testid="exacta-ev-override"
                className="ml-1 w-16 bg-slate-800 border border-amber-600/40 rounded px-2 py-1 text-sm text-amber-200" />
            </label>
          )}
          <label className="text-sm text-slate-400" title="Your auto-refresh interval (saved for next time)">
            Refresh
            <select value={refreshSec}
              onChange={(e) => { const v = Number(e.target.value); setRefreshSec(v); localStorage.setItem("exacta_refresh_sec", String(v)); }}
              data-testid="exacta-refresh-interval"
              className="ml-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100">
              <option value={15}>15s</option>
              <option value={30}>30s</option>
              <option value={60}>60s</option>
              <option value={0}>Off</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-300 cursor-pointer select-none"
            title="Hide races that are already final / graded">
            <input type="checkbox" checked={upcomingOnly}
              onChange={(e) => setUpcomingOnly(e.target.checked)}
              data-testid="upcoming-only-toggle"
              className="accent-emerald-500 w-4 h-4" />
            Upcoming only
          </label>
          <label className="text-sm text-slate-400">Track
            <select value={trackFilter} onChange={(e) => setTrackFilter(e.target.value)}
              data-testid="track-filter"
              className="ml-2 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100">
              <option value="all">All tracks</option>
              {trackOptions.map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </label>
        </div>

        {/* cumulative banner — graded races for the current track filter */}
        {settledRaces.length > 0 && (
          <div data-testid="exacta-cumulative"
            className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/70 px-4 py-3">
            <span className="text-sm font-semibold text-slate-300">Graded results ({settledRaces.length})</span>
            <RoiStat label="Wagered" value={`$${cumW.toFixed(2)}`} />
            <RoiStat label="Returned" value={`$${cumR.toFixed(2)}`} />
            <RoiStat label="ROI" value={cumRoi == null ? "—" : `${cumRoi}%`}
              tone={cumRoi == null ? "slate" : cumRoi >= 0 ? "green" : "red"} testid="cumulative-roi" />
            <span className="text-xs text-slate-500">${parseFloat(wager) > 0 ? parseFloat(wager) : 100} dutch per race · {isAdmin ? `${evPct}%` : "20%"} edge gate · fair = projodds</span>
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-slate-400 py-10 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        )}
        {error && <p className="text-red-400 text-sm" data-testid="exacta-error">{error}</p>}

        {!loading && data && listRaces.length === 0 && (
          <div data-testid="exacta-empty" className="text-center text-slate-500 py-16">
            {races.length === 0
              ? `No exacta-value snapshots for ${date}.`
              : "No races match the current filters."}
            {races.length === 0 && (
              <p className="text-xs mt-1">Snapshots are captured at each race's first POST-status cycle as cards run.</p>
            )}
          </div>
        )}

        <div className="space-y-3">
          {listRaces.map((r) => <RaceCard key={r.race_key} race={r} date={date} isAdmin={isAdmin} />)}
        </div>
      </main>
    </div>
  );
}
